/**
 * COLUMN TYPE DEFINITIONS
 * Defines all possible column configurations for universal table
 * @version 1.0.0
 * @production-ready
 */

export type ColumnType = 
  | 'text'        // Plain text input
  | 'number'      // Numeric input with decimals
  | 'time'        // Time picker (HH:MM)
  | 'date'        // Date picker
  | 'select'      // Dropdown selection
  | 'readonly'    // Display-only field
  | 'calculated'  // Auto-calculated field
  | 'action';     // Action button (e.g., delete)

export type WidthMode = 'auto' | 'fixed' | number;

/**
 * Complete column definition
 */
export interface ColumnDefinition {
  // Identity
  key: string;                              // Unique column identifier
  label: string;                            // Full column label
  type: ColumnType;                         // Column input type
  backendKey?: string;                      // Backend field name (if different from key)
  
  // Width Configuration
  width?: WidthMode;                        // 'auto', 'fixed', or specific px
  minWidth?: number;                        // Minimum width in pixels
  maxWidth?: number;                        // Maximum width in pixels
  
  // Layout
  sticky?: boolean;                         // Stick column to left
  hidden?: boolean;                         // Hide column
  order?: number;                           // Display order
  
  // Behavior
  editable?: boolean;                       // Allow editing (default: true)
  calculated?: boolean;                     // Is auto-calculated
  required?: boolean;                       // Required field
  disabled?: boolean;                       // Disable input
  
  // Display
  abbreviation?: string;                    // Short label for condensed view
  placeholder?: string;                     // Input placeholder
  suffix?: string;                          // Display suffix (e.g., "kWh", "°C")
  prefix?: string;                          // Display prefix (e.g., "$")
  unitLabel?: string;                       // Optional unit description for header sub-row
  
  // Formatting & Validation
  format?: (value: any, rowData?: any) => string;
  parse?: (value: string) => any;
  validate?: (value: any, rowData?: any) => boolean | string;
  decimalPlaces?: number;                    // Number of decimal places for number formatting (default: 2)
  
  // Select-specific
  options?: string[] | { label: string; value: any }[];
  
  // Calculated fields
  calculateFn?: (rowData: any, allData?: any) => any;
  dependencies?: string[];                  // Which columns trigger recalculation
  
  // Styling
  headerColor?: string;
  cellColor?: string;
  fontWeight?: number;
  textAlign?: 'left' | 'center' | 'right';
  
  // Metadata
  description?: string;                     // Tooltip description
  group?: string;                           // Column group for organization
}

/**
 * Column group for organized headers
 */
export interface ColumnGroup {
  label: string;
  columns: ColumnDefinition[];
  color?: string;
  collapsed?: boolean;
}

/**
 * Column preset configurations
 */
export const COLUMN_PRESETS = {
  DATE: {
    type: 'date' as ColumnType,
    sticky: true,
    width: 'auto' as WidthMode,
    minWidth: 110,
    editable: true
  },
  TIME: {
    type: 'time' as ColumnType,
    width: 'auto' as WidthMode,
    minWidth: 90
  },
  NUMBER: {
    type: 'number' as ColumnType,
    width: 'auto' as WidthMode,
    minWidth: 80,
    textAlign: 'center' as const
  },
  CALCULATED: {
    type: 'calculated' as ColumnType,
    editable: false,
    calculated: true,
    width: 'auto' as WidthMode,
    minWidth: 90
  },
  READONLY: {
    type: 'readonly' as ColumnType,
    editable: false,
    width: 'auto' as WidthMode
  }
};
